import { buildQuery } from '@calendly/http-tools'

/*
 * App URLs
 */

export const getScope = (type) => (type === 'page' ? 'team' : type)

export const getWorkflowsAppBasePath = (isRebrandEnabled = false) => (
  isRebrandEnabled ? '/app/automations' : '/app/workflows'
)

/** Last path segment for the template gallery (`/users/me/...`) — rebrand uses `automation_templates`. */
export const workflowsTemplatesUrlSegment = (isRebrandEnabled = false) => (
  isRebrandEnabled ? 'automation_templates' : 'workflow_templates'
)

/** Sub-path segment for individual workflow routes (`…/workflows/new`, etc.) — rebrand uses `automations`. */
const workflowsSubPathSegment = (isRebrandEnabled = false) => (
  isRebrandEnabled ? 'automations' : 'workflows'
)

export const appWorkflowListUrl = (workflowOwner, canAccessManagedWorkflows, isRebrandEnabled = false) => {
  const base = getWorkflowsAppBasePath(isRebrandEnabled)
  if (!['organization', 'user_group'].includes(workflowOwner.type)) {
    return `${base}/${getScope(workflowOwner.type)}/${workflowOwner.id}`
  }

  if (canAccessManagedWorkflows) return '/app/admin/managed_workflows'

  return `${base}/user/me?admin_managed=true`
}

export const appWorkflowTemplatesListUrl = ({ id, type }, isRebrandEnabled = false) => {
  const scope = getScope(type)
  const base = getWorkflowsAppBasePath(isRebrandEnabled)

  if (type === 'organization') return '/app/admin/managed_workflows/workflow_templates'

  return `${base}/${scope}s/${id}/${workflowsTemplatesUrlSegment(isRebrandEnabled)}`
}

export const appNewWorkflowUrl = ({ type, id }, isRebrandEnabled = false) => {
  const scope = getScope(type)
  const base = getWorkflowsAppBasePath(isRebrandEnabled)

  if (type === 'organization') return '/app/admin/managed_workflows/new'

  return `${base}/${scope}s/${id}/${workflowsSubPathSegment(isRebrandEnabled)}/new`
}

export const appCloneWorkflowUrl = (owner, originalWorkflowUuid, isRebrandEnabled = false) => (
  `${appNewWorkflowUrl(owner, isRebrandEnabled)}?${buildQuery({ originalWorkflowUuid })}`
)

export const appNewWorkflowFromTemplateUrl = (owner, templateId, backTo, isRebrandEnabled = false) => (
  `${appNewWorkflowUrl(owner, isRebrandEnabled)}?${buildQuery({ templateId, backTo })
  }`
)

export const appEditWorkflowUrl = ({ uuid, ownerType, ownerId }, isRebrandEnabled = false) => {
  if (ownerType === 'organization') return `/app/admin/managed_workflows/${uuid}/edit`
  if (ownerType === 'user_group') return `/app/admin/managed_workflows/user_groups/${ownerId}/workflows/${uuid}/edit`

  const scope = getScope(ownerType)
  const base = getWorkflowsAppBasePath(isRebrandEnabled)
  return `${base}/${scope}s/${ownerId}/${workflowsSubPathSegment(isRebrandEnabled)}/${uuid}/edit`
}

const appViewManagedWorkflowUrl = (uuid, isRebrandEnabled = false) => (
  `${getWorkflowsAppBasePath(isRebrandEnabled)}/organization/${workflowsSubPathSegment(isRebrandEnabled)}/${uuid}`
)

export const appViewWorkflowUrl = ({
  uuid, ownerType, ownerId, isAdminOrOwner, isGroupAdmin, isRebrandEnabled = false
}) => {
  if (ownerType === 'organization' && !isAdminOrOwner) {
    return appViewManagedWorkflowUrl(uuid, isRebrandEnabled)
  }
  if (ownerType === 'user_group' && !isGroupAdmin && !isAdminOrOwner) {
    return `${appViewManagedWorkflowUrl(uuid, isRebrandEnabled)}?group_uuid=${ownerId}`
  }

  return appEditWorkflowUrl({ uuid, ownerType, ownerId }, isRebrandEnabled)
}

/*
 * API Urls
 */

export const apiOwnerListLegacyUrl = '/api/workflows/owners'

export const apiOwnerListUrl = '/api/apps/workflows/owners'
export const apiManagedWorkflowsUrl = '/api/apps/workflows/managed_workflows'

export const apiCreateWorkflowUrl = (owner, { workflowsExtractionEnabled = false }) => {
  if (workflowsExtractionEnabled) {
    const query = owner.type === 'organization'
      ? { owner_type: 'organization' }
      : { owner_id: owner.id, owner_type: owner.type }
    return `/api/apps/workflows/workflows?${buildQuery(query)}`
  }

  return owner.type === 'organization'
    ? '/api/organization/workflows'
    : `/api/${owner.type}s/${owner.id}/workflows`
}

export const apiUpdateWorkflowUrl = (owner, uuid, { workflowsExtractionEnabled = false }) => {
  if (workflowsExtractionEnabled) {
    return `/api/apps/workflows/workflows/${uuid}`
  }

  return owner.type === 'organization'
    ? `/api/organization/workflows/${uuid}`
    : `/api/${owner.type}s/${owner.id}/workflows/${uuid}`
}

export const apiDeleteWorkflowUrl = (owner, uuid, { workflowsExtractionEnabled = false }) => {
  if (workflowsExtractionEnabled) {
    return `/api/apps/workflows/workflows/${uuid}`
  }

  return (
    owner.type === 'organization'
      ? `/api/organization/workflows/${uuid}`
      : `/api/${owner.type}s/${owner.id}/workflows/${uuid}`
  )
}

export const apiWorkflowShowUrl = (owner, uuid) => (
  owner.type === 'organization'
    ? `/api/organization/workflows/${uuid}`
    : `/api/${owner.type}s/${owner.id}/workflows/${uuid}`
)

export const apiWorkflowEditUrl = (owner, uuid) => (
  owner.type === 'organization'
    ? `/api/organization/workflows/${uuid}/edit`
    : `/api/${owner.type}s/${owner.id}/workflows/${uuid}/edit`
)
export const apiWorkflowNewUrl = (owner) => (
  owner.type === 'organization'
    ? '/api/organization/workflows/new'
    : `/api/${owner.type}s/${owner.id}/workflows/new`
)

export const apiOwnerEventTypesUrl = '/api/apps/workflows/event_types'

export const apiEventTypesWorkflowUrl = (type, ownerType, ownerId, eventTypeId) => (
  type === 'ManagedEventType'
    ? `/api/organization/managed_event_types/${eventTypeId}/workflows`
    : `/api/${ownerType.toLowerCase()}s/${ownerId}/event_types/${eventTypeId}/workflows`
)

/*
 * External entry point URLs
 */
export const outlookConnectedPageUrl = '/integrations/outlook/edit'

/*
 * Utility methods
 */

export const parseParams = ({ params: { owner_type: ownerType, owner_id: ownerId } }) => ({
  owner: {
    id: ownerId,
    type: ownerType === 'users' ? 'user' : 'team'
  }
})
